import { VideoLibraryRounded } from '@mui/icons-material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  LinearProgress,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AUTHORITIES } from 'app/config/constants';
import { useAppSelector } from 'app/config/store';
import {
  CameraPlaybackTimeline,
  CameraRecording,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-cameras-tab/CameraPlaybackTimeline';
import {
  HatJsVideoPlayer,
  HatJsVideoPlayerHandle,
  HatJsVideoQuality,
} from 'app/modules/dashboards/dashboard-truck-implement/components/truck-implement-cameras-tab/HatJsVideoPlayer';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import SelectPaginateFromPaginatedApi from 'app/shared/components/select-paginate/SelectPaginateFromPaginatedApi';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

interface VehicleOption {
  id: number;
  licensePlate?: string;
  deviceStatus?: number | null;
  terminalID?: string;
}

interface CompanyOption {
  id: number;
  name?: string;
}

interface CameraChannel {
  id: number;
  channelNumber: number;
  displayName?: string;
  timeZoneOffsetMinutes?: number;
}

interface CameraPlaybackStream {
  channelId: number;
  url: string;
  contentType: string;
  startedAt: string;
  endedAt: string;
  inactive?: boolean;
  recordingStartedAt?: string;
  recordingEndedAt?: string;
}

interface CameraDownloadTask {
  taskId?: string;
  status: string;
  source?: 'SDK_DOWNLOAD_TASK';
  progress?: number;
  downloadUrl?: string;
}

type CameraRecordingResponse = Omit<CameraRecording, 'channelId'>;
type SidebarMode = 'filters' | 'recordings';

const DEFAULT_STORE_TYPE = 0;
const DOWNLOAD_POLL_INTERVAL_MS = 4000;
const DOWNLOAD_MAX_ATTEMPTS = 45;
const MAX_CUSTOM_PERIOD_MS = (24 * 60 + 30) * 60 * 1000;
const PLAYER_ASPECT_RATIO = 16 / 9;
const PLAYER_BORDER_SIZE = 2;
const MIN_PLAYER_WIDTH = 480;
const VEHICLE_API_URL = 'api/admin/camera-devices/options';
const SIDEBAR_PADDING = 2;

const DEFAULT_DEVICE_OFFSET_MINUTES = -180;

const pad2 = (value: number) => String(value).padStart(2, '0');

// Todos os horários da tela são "hora de parede" do dispositivo. As funções abaixo convertem
// entre essa hora de parede (string YYYY-MM-DDTHH:mm) e o Instant absoluto (ISO/UTC) que trafega
// com o backend e com a HAT, usando o offset do dispositivo (em minutos, ex.: -180 para UTC-3).

const instantToDeviceWall = (date: Date, offsetMinutes: number) => {
  const shifted = new Date(date.getTime() + offsetMinutes * 60_000);
  return (
    `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}` +
    `T${pad2(shifted.getUTCHours())}:${pad2(shifted.getUTCMinutes())}`
  );
};

const deviceWallToInstant = (wall: string, offsetMinutes: number): Date => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(wall);
  if (!match) return new Date(NaN);
  const [, year, month, day, hour, minute, second] = match;
  const utcMs = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), second ? Number(second) : 0);
  return new Date(utcMs - offsetMinutes * 60_000);
};

const formatInDeviceZone = (date: Date, offsetMinutes: number, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('pt-BR', { ...options, timeZone: 'UTC' }).format(new Date(date.getTime() + offsetMinutes * 60_000));

/**
 * Converte um Instant (ISO/UTC vindo do backend) para a "hora de parede" do dispositivo no
 * formato que a HAT espera: "YYYY-MM-DD HH:mm:ss". O player HATJS repassa esse formato sem
 * reconvertê-lo, garantindo que o playback comece no horário correto do dispositivo.
 */
const toDeviceWallClockSeconds = (iso: string, offsetMinutes: number): string => {
  const shifted = new Date(new Date(iso).getTime() + offsetMinutes * 60_000);
  if (Number.isNaN(shifted.getTime())) return iso;
  return (
    `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())} ` +
    `${pad2(shifted.getUTCHours())}:${pad2(shifted.getUTCMinutes())}:${pad2(shifted.getUTCSeconds())}`
  );
};

/**
 * Normaliza um timestamp de gravação vindo do HAT Cloud para ISO/UTC. O HAT devolve a hora de
 * parede do dispositivo como "YYYY-MM-DD HH:mm:ss" (sem fuso), que o backend não consegue parsear
 * com Instant.parse. Interpretamos essa parede no fuso do dispositivo e devolvemos ISO. Valores já
 * em ISO (com "T"/"Z") são reconhecidos por new Date e retornados como ISO também.
 */
const normalizeHatTimestamp = (value: string | null | undefined, offsetMinutes: number): string => {
  if (!value) return value ?? '';
  // Formato do HAT: "YYYY-MM-DD HH:mm:ss" (com espaço, sem fuso) → parede do dispositivo.
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(value)) {
    const instant = deviceWallToInstant(value, offsetMinutes);
    return Number.isNaN(instant.getTime()) ? value : instant.toISOString();
  }
  // Já é ISO (ou outro formato reconhecido pelo Date): normaliza para ISO/UTC.
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

const defaultStartedAt = (offsetMinutes: number) => instantToDeviceWall(new Date(Date.now() - 60 * 60 * 1000), offsetMinutes);
const defaultEndedAt = (offsetMinutes: number) => instantToDeviceWall(new Date(), offsetMinutes);

const presetPeriod = (hours: number, offsetMinutes: number) => {
  const end = new Date();
  return {
    startedAt: instantToDeviceWall(new Date(end.getTime() - hours * 60 * 60 * 1000), offsetMinutes),
    endedAt: instantToDeviceWall(end, offsetMinutes),
  };
};

const formatRecordingTimeRange = (startedAt: string, endedAt: string, offsetMinutes: number) => {
  const start = new Date(startedAt);
  const end = new Date(endedAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '-';

  const formatTime = (value: Date) => formatInDeviceZone(value, offsetMinutes, { hour: '2-digit', minute: '2-digit' });
  return `${formatTime(start)} → ${formatTime(end)}`;
};

const formatRecordingDateRange = (startedAt: string, endedAt: string, offsetMinutes: number) => {
  const start = new Date(startedAt);
  const end = new Date(endedAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '-';

  const formatDate = (value: Date) => formatInDeviceZone(value, offsetMinutes, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatShortDate = (value: Date) => formatInDeviceZone(value, offsetMinutes, { day: '2-digit', month: '2-digit' });
  const sameDay = formatDate(start) === formatDate(end);
  return sameDay ? formatDate(start) : `${formatShortDate(start)} → ${formatShortDate(end)}`;
};

const formatBytes = (value: number) => {
  if (!value) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  return `${(value / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const recordingKey = (recording: CameraRecording) => `${recording.channelId}:${recording.startedAt}:${recording.endedAt}`;

const channelLabel = (channel: CameraChannel) => channel.displayName || `Câmera ${channel.channelNumber}`;

const vehicleStatusLabel = (status?: number | null) => {
  if (status === 1) return 'Online';
  if (status === 0) return 'Offline';
  if (status === 2) return 'Em repouso';
  return 'Desconhecido';
};

const vehicleOptionLabel = (option?: VehicleOption | null) => {
  const licensePlate = option?.licensePlate || String(option?.id || '');
  const status = vehicleStatusLabel(option?.deviceStatus);
  return status ? `${licensePlate} · ${status}` : licensePlate;
};

const vehicleStatusChipSx = (status: number | null | undefined, theme: Theme) => {
  if (status === 1) {
    return { color: theme.palette.success.main, backgroundColor: alpha(theme.palette.success.main, 0.12) } as const;
  }
  if (status === 0) {
    return { color: theme.palette.text.secondary, backgroundColor: alpha(theme.palette.text.secondary, 0.12) } as const;
  }
  if (status === 2) {
    return { color: theme.palette.warning.main, backgroundColor: alpha(theme.palette.warning.main, 0.12) } as const;
  }
  return { color: theme.palette.text.secondary, backgroundColor: theme.palette.action.selected } as const;
};

const renderVehicleOption = (option: VehicleOption | null | undefined, theme: Theme) => {
  const status = vehicleStatusLabel(option?.deviceStatus);
  const chipStyle = vehicleStatusChipSx(option?.deviceStatus, theme);
  const licensePlate = option?.licensePlate || String(option?.id || '');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, width: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{licensePlate}</div>
      <div
        style={{
          ...chipStyle,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 22,
          minHeight: 22,
          minWidth: 58,
          padding: '0 8px',
          borderRadius: 12,
          fontSize: '0.6875rem',
          fontWeight: 600,
          lineHeight: '22px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {status}
      </div>
    </div>
  );
};

const GENERIC_CAMERA_ERROR = 'Não foi possível conectar à câmera. Tente novamente mais tarde.';

const messageFromError = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) return fallback;

  const responseDetail = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : '';
  const responseMessage = typeof error.response?.data?.message === 'string' ? error.response.data.message : '';
  const candidate = responseDetail || responseMessage;
  const isTechnicalMessage = /request failed with status code|network error|timeout|hat cloud|error\.http\./i.test(candidate || error.message);

  return candidate && !isTechnicalMessage ? candidate : GENERIC_CAMERA_ERROR;
};

const getVideoColumnWidth = (layout: HTMLElement): number => {
  const layoutWidth = layout.getBoundingClientRect().width;
  const layoutStyle = window.getComputedStyle(layout);
  const gridColumns = layoutStyle.gridTemplateColumns.split(/\s+/);
  const sidebarWidth = Number.parseFloat(gridColumns[0] || '') || 360;
  const lastGridColumn = gridColumns[gridColumns.length - 1] || '';
  const computedContentWidth = lastGridColumn.endsWith('px') ? Number.parseFloat(lastGridColumn) : Number.NaN;
  const fallbackContentWidth = layoutWidth - sidebarWidth - (Number.parseFloat(layoutStyle.columnGap) || 0);
  return Number.isFinite(computedContentWidth) ? computedContentWidth : fallbackContentWidth;
};

const compactSidebarSectionSx = {
  px: 0,
  py: 0,
  backgroundColor: 'transparent',
};

const DownloadingIndicator: React.FC<{ size: number; onCancel: () => void }> = ({ size, onCancel }) => (
  <Box
    onClick={event => {
      event.stopPropagation();
      onCancel();
    }}
    role="button"
    aria-label="Cancelar exportação"
    sx={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      cursor: 'pointer',
      color: 'error.main',
      transition: 'opacity 120ms',
      '&:hover': { opacity: 0.75 },
    }}
  >
    <CircularProgress size={size} thickness={4} sx={{ color: 'secondary.main', position: 'absolute' }} />
    <CloseRoundedIcon sx={{ fontSize: size * 0.7, position: 'absolute' }} />
  </Box>
);

const RecordingDownloadAction: React.FC<{
  title: string;
  isThisDownloading: boolean;
  disabled: boolean;
  onDownload: () => void;
  onCancel: () => void;
}> = ({ title, isThisDownloading, disabled, onDownload, onCancel }) => {
  const theme = useTheme();
  if (isThisDownloading) {
    return (
      <Tooltip title="Cancelar exportação">
        <Box sx={{ p: 0.5, display: 'inline-flex' }}>
          <DownloadingIndicator size={16} onCancel={onCancel} />
        </Box>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Baixar gravação">
      <IconButton
        aria-label={`Baixar gravação de ${title}`}
        disabled={disabled}
        size="small"
        onClick={event => {
          event.stopPropagation();
          onDownload();
        }}
        sx={{
          color: alpha(theme.palette.text.primary, 0.55),
          p: 0.5,
          '&:hover': { color: 'secondary.main', backgroundColor: alpha(theme.palette.secondary.main, 0.1) },
          '&.Mui-disabled': { color: alpha(theme.palette.text.primary, 0.26) },
        }}
      >
        <DownloadOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  );
};

const MassDownloadButton: React.FC<{
  isDownloading: boolean;
  hasSelection: boolean;
  onStart: () => void;
  onCancel: () => void;
}> = ({ isDownloading, hasSelection, onStart, onCancel }) => {
  const theme = useTheme();
  const label = isDownloading ? 'Cancelar exportação' : 'Baixar gravações selecionadas';
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          color="secondary"
          disabled={!isDownloading && !hasSelection}
          onClick={isDownloading ? onCancel : onStart}
          aria-label={label}
          sx={{
            flexShrink: 0,
            width: 38,
            height: 38,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
            borderRadius: '8px',
            '&.Mui-disabled': { borderColor: theme.palette.divider },
          }}
        >
          {isDownloading ? <DownloadingIndicator size={18} onCancel={onCancel} /> : <DownloadOutlinedIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const SectionLabel: React.FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {hint}
      </Typography>
    )}
  </Stack>
);

export const CameraPlayback: React.FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const fixedVehicleParams = new URLSearchParams(history.location.search);
  const fixedTruckImplementId = Number(fixedVehicleParams.get('truckImplementId')) || null;
  const fixedLicensePlate = fixedVehicleParams.get('licensePlate') || '';
  const fixedCompanyId = Number(fixedVehicleParams.get('companyId')) || null;
  const fixedCompanyName = fixedVehicleParams.get('companyName') || '';
  const fixedChannelIds = fixedVehicleParams.get('channelIds')?.split(',').map(Number).filter(Number.isFinite) ?? [];

  const [company, setCompany] = useState<CompanyOption | null>(null);
  const [vehicle, setVehicle] = useState<VehicleOption | null>(null);
  const [vehicleListRefreshKey, setVehicleListRefreshKey] = useState(0);
  const [refreshingVehicleList, setRefreshingVehicleList] = useState(false);
  const [device, setDevice] = useState<any>(null); // Device carregado da HAT Cloud
  const [channels, setChannels] = useState<CameraChannel[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const vehicleApiBaseUrl = isAdmin && company?.id ? `${VEHICLE_API_URL}?searchCompanyId=${company.id}` : VEHICLE_API_URL;
  const vehicleApiUrl = `${vehicleApiBaseUrl}${vehicleApiBaseUrl.includes('?') ? '&' : '?'}refreshKey=${vehicleListRefreshKey}`;

  const [startedAt, setStartedAt] = useState(() => defaultStartedAt(DEFAULT_DEVICE_OFFSET_MINUTES));
  const [endedAt, setEndedAt] = useState(() => defaultEndedAt(DEFAULT_DEVICE_OFFSET_MINUTES));
  const [selectedPresetHours, setSelectedPresetHours] = useState<number | null>(1);
  const [customPeriodOpen, setCustomPeriodOpen] = useState(false);
  const [customStartedAt, setCustomStartedAt] = useState<string | null>(null);
  const [customEndedAt, setCustomEndedAt] = useState<string | null>(null);

  // Fuso do dispositivo (em minutos). Todos os horários da tela são "hora de parede" deste fuso.
  const deviceOffsetMinutes = channels.find(channel => channel.timeZoneOffsetMinutes != null)?.timeZoneOffsetMinutes ?? DEFAULT_DEVICE_OFFSET_MINUTES;

  const [recordings, setRecordings] = useState<CameraRecording[]>([]);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('filters');
  const [showRecordingHint, setShowRecordingHint] = useState(true);
  const [selectedRecordingKeys, setSelectedRecordingKeys] = useState<string[]>([]);
  const [expandedRecordingGroups, setExpandedRecordingGroups] = useState<Record<number, boolean>>({});
  const [activeRecording, setActiveRecording] = useState<CameraRecording | null>(null);

  const [playback, setPlayback] = useState<CameraPlaybackStream[] | null>(null);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState<Date | null>(null);
  const autoAdvancePlaybackKeyRef = useRef<string | null>(null);
  const quality: HatJsVideoQuality = 'SUB';

  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [loadingPlayback, setLoadingPlayback] = useState(false);

  // Download. Os downloads das gravações são processados em fila (um de cada vez), reaproveitando a
  // máquina de tarefa/polling existente. `downloadingKeys` rastreia POR GRAVAÇÃO quem está na fila/
  // baixando (para o spinner aparecer só no card certo).
  const [downloadingKeys, setDownloadingKeys] = useState<string[]>([]);
  const [downloadPhase, setDownloadPhase] = useState<'preparing' | 'transferring'>('preparing');
  const [downloadQueueTotal, setDownloadQueueTotal] = useState(0);
  const [downloadQueueIndex, setDownloadQueueIndex] = useState(1);
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [isPlayerFullscreen, setIsPlayerFullscreen] = useState(false);

  const [playerMaxWidth, setPlayerMaxWidth] = useState<number | null>(null);
  const [playerMaxHeight, setPlayerMaxHeight] = useState<number | null>(null);
  const monitorLayoutRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLElement>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playbackPlayerRef = useRef<HatJsVideoPlayerHandle>(null);
  const downloadGenerationRef = useRef(0);
  const buildCameraNavigationQuery = () => {
    const params = new URLSearchParams();
    params.set('navigation', 'sidebar');
    if (vehicle?.id) params.set('truckImplementId', String(vehicle.id));
    if (vehicle?.licensePlate) params.set('licensePlate', vehicle.licensePlate);
    if (company?.id) params.set('companyId', String(company.id));
    if (company?.name) params.set('companyName', company.name);
    if (selectedChannelIds.length) params.set('channelIds', selectedChannelIds.join(','));
    return params.toString();
  };
  const fixedVehicleQuery = fixedTruckImplementId
    ? `truckImplementId=${fixedTruckImplementId}${fixedLicensePlate ? `&licensePlate=${encodeURIComponent(fixedLicensePlate)}` : ''}${
        fixedCompanyId ? `&companyId=${fixedCompanyId}` : ''
      }${fixedCompanyName ? `&companyName=${encodeURIComponent(fixedCompanyName)}` : ''}${
        fixedChannelIds.length ? `&channelIds=${fixedChannelIds.join(',')}` : ''
      }`
    : '';
  const liveMonitorRoute = fixedVehicleQuery ? `/live-monitor?${fixedVehicleQuery}` : '/live-monitor';
  // taskId da exportação atualmente em processamento no HAT, para permitir cancelamento.
  const activeDownloadTaskIdRef = useRef<string | null>(null);
  const activeFileDownloadCancelRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);

  useEffect(() => {
    if (fixedCompanyId && !company) setCompany({ id: fixedCompanyId, name: fixedCompanyName || undefined });
  }, [company, fixedCompanyId, fixedCompanyName]);

  useEffect(() => {
    if (!fixedTruckImplementId) return;
    setVehicle(current =>
      current?.id === fixedTruckImplementId && current.licensePlate === fixedLicensePlate
        ? current
        : { id: fixedTruckImplementId, licensePlate: fixedLicensePlate || undefined }
    );
  }, [fixedLicensePlate, fixedTruckImplementId]);

  const refreshVehicleList = async () => {
    setRefreshingVehicleList(true);
    try {
      const { data } = await axios.get(vehicleApiBaseUrl, {
        params: {
          page: 0,
          size: 10,
          sort: 'licensePlate',
          searchLicensePlate: '',
          cacheBuster: new Date().getTime(),
        },
      });
      setVehicle(current => {
        if (!current) return current;
        const refreshedVehicle = (data.content || []).find((option: VehicleOption) => option.id === current.id);
        return refreshedVehicle || current;
      });
      setVehicleListRefreshKey(current => current + 1);
    } finally {
      setRefreshingVehicleList(false);
    }
  };

  useEffect(() => {
    if (!fixedTruckImplementId) return;
    void refreshVehicleList();
  }, [fixedTruckImplementId, vehicleApiBaseUrl]);

  useEffect(() => {
    const form = formRef.current;
    const layout = monitorLayoutRef.current;
    if (!form || !layout) return undefined;

    const updatePlayerBounds = () => {
      const formHeight = form.getBoundingClientRect().height;
      const availableWidth = getVideoColumnWidth(layout);
      const playerBounds = playerContainerRef.current?.getBoundingClientRect();
      const controlsHeight = playerBounds ? Math.max(playerBounds.height - playerBounds.width * PLAYER_ASPECT_RATIO, 0) : 0;
      const maxWidthByHeight = Math.max((formHeight - controlsHeight - PLAYER_BORDER_SIZE) * PLAYER_ASPECT_RATIO + PLAYER_BORDER_SIZE, 1);
      setPlayerMaxHeight(Math.max(formHeight, 1));
      setPlayerMaxWidth(Math.max(Math.min(availableWidth, maxWidthByHeight), 1));
    };

    updatePlayerBounds();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePlayerBounds);
      return () => window.removeEventListener('resize', updatePlayerBounds);
    }

    const observer = new ResizeObserver(updatePlayerBounds);
    observer.observe(form);
    observer.observe(layout);
    if (playerContainerRef.current) observer.observe(playerContainerRef.current);
    return () => observer.disconnect();
  }, [playback?.length]);

  useEffect(() => {
    let active = true;
    setChannels([]);
    setSelectedChannelIds([]);
    setRecordings([]);
    setSelectedRecordingKeys([]);
    setSidebarMode('filters');
    setPlayback(null);
    setActiveRecording(null);
    setError(null);
    setDevice(null);

    if (!vehicle) return undefined;

    setLoadingChannels(true);
    void (async () => {
      try {
        const response = await axios.get<any>('api/admin/camera-devices', { params: { size: 1, truckImplementId: vehicle.id } });
        if (!active) return;

        const results = response.data.data?.results || response.data.results || [];
        const matchedDevice = results.find((d: any) => d.truckImplementId === vehicle.id && d.deviceChannelList);

        if (matchedDevice && matchedDevice.deviceChannelList && Array.isArray(matchedDevice.deviceChannelList)) {
          if (matchedDevice.deviceStatus !== 1) {
            setError(
              matchedDevice.deviceStatus === 0
                ? 'Este veículo está offline no momento. Aguarde alguns instantes e tente novamente quando o equipamento estiver online.'
                : matchedDevice.deviceStatus === 2
                ? 'A câmera deste veículo está em repouso. O playback estará disponível quando ela estiver online.'
                : 'Não foi possível confirmar o status da câmera. Tente novamente mais tarde.'
            );
            return;
          }

          if (matchedDevice.timeZone == null) {
            setError('O fuso horário (timeZone) deste equipamento não foi definido. Configure o timeZone do dispositivo para usar o playback.');
            return;
          }

          setDevice(matchedDevice);

          const disabledChannelNumbers: number[] = matchedDevice.disabledChannelNumbers || [];
          const videoChannels = matchedDevice.deviceChannelList.filter(
            (ch: any) => ch.channelType === 0 && !disabledChannelNumbers.includes(Number(ch.channelNum))
          );

          const formattedChannels: CameraChannel[] = videoChannels.map((ch: any, idx: number) => ({
            id: Number(ch.channelNum) || idx + 1,
            channelNumber: Number(ch.channelNum) || idx + 1,
            displayName: ch.channelName || undefined,
            timeZoneOffsetMinutes: matchedDevice.timeZone * 60, // Converte horas em minutos
          }));

          setChannels(formattedChannels);
          setSelectedChannelIds(
            fixedChannelIds.length ? formattedChannels.filter(channel => fixedChannelIds.includes(channel.id)).map(channel => channel.id) : []
          );
        } else {
          setError('Este veículo não possui câmeras vinculadas.');
        }
      } catch (requestError) {
        if (active) setError(messageFromError(requestError, 'Não foi possível carregar as câmeras do veículo.'));
      } finally {
        if (active) setLoadingChannels(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [vehicle]);

  // ── Reaplica o período no fuso do dispositivo quando ele passa a ser conhecido ──────────────
  // Enquanto o usuário estiver usando um preset (não editou manualmente), reprojeta os inputs no
  // offset correto do device. Assim "1h" significa a última hora no relógio do dispositivo.
  useEffect(() => {
    if (selectedPresetHours === null) return;
    const preset = presetPeriod(selectedPresetHours, deviceOffsetMinutes);
    setStartedAt(preset.startedAt);
    setEndedAt(preset.endedAt);
  }, [deviceOffsetMinutes]);

  useEffect(
    () => () => {
      downloadGenerationRef.current += 1;
    },
    []
  );

  const toggleChannel = (channelId: number) => {
    setError(null);
    setSelectedChannelIds(current => {
      if (current.includes(channelId)) return current.filter(id => id !== channelId);
      return [...current, channelId];
    });
  };

  const toggleRecordingSelection = (recording: CameraRecording) => {
    const key = recordingKey(recording);
    setSelectedRecordingKeys(current => (current.includes(key) ? current.filter(selectedKey => selectedKey !== key) : [...current, key]));
  };

  const toggleRecordingGroupSelection = (items: CameraRecording[]) => {
    if (playbackControlsLocked || !items.length) return;
    const groupKeys = items.map(recordingKey);
    const groupKeysSet = new Set(groupKeys);
    setSelectedRecordingKeys(current => {
      const isGroupSelected = groupKeys.every(key => current.includes(key));
      if (isGroupSelected) return current.filter(key => !groupKeysSet.has(key));
      return Array.from(new Set([...current, ...groupKeys]));
    });
  };

  const loadRecordings = async () => {
    if (!vehicle) return;
    if (!device || device.timeZone == null) {
      setError('O fuso horário (timeZone) deste equipamento não foi definido. Configure o timeZone do dispositivo para usar o playback.');
      return;
    }
    if (!selectedChannelIds.length) {
      setError('Selecione pelo menos uma câmera para buscar gravações.');
      return;
    }

    // Os inputs são hora de parede do dispositivo; converte para Instant absoluto no offset dele.
    const start = deviceWallToInstant(startedAt, deviceOffsetMinutes);
    const end = deviceWallToInstant(endedAt, deviceOffsetMinutes);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setError('Preencha o início e o fim do período.');
      return;
    }

    if (end.getTime() <= start.getTime()) {
      setError('O fim do período deve ser posterior ao início.');
      return;
    }

    if (end.getTime() - start.getTime() > MAX_CUSTOM_PERIOD_MS) {
      setError('O período de busca não pode exceder 24 horas.');
      return;
    }

    setError(null);
    setRecordings([]);
    setSelectedRecordingKeys([]);
    setPlayback(null);
    setActiveRecording(null);
    setCurrentPlaybackTime(null);
    setLoadingRecordings(true);

    try {
      const responses = await Promise.all(
        selectedChannelIds.map(channelId =>
          axios.get<CameraRecordingResponse[]>(`api/cameras/channels/${channelId}/recordings`, {
            params: {
              deviceCode: device?.terminalID,
              startedAt: start.toISOString(),
              endedAt: end.toISOString(),
              timeZoneOffsetMinutes: device.timeZone * 60,
              storeType: DEFAULT_STORE_TYPE,
            },
          })
        )
      );

      const combined = responses
        .reduce<CameraRecording[]>((acc, response, index) => {
          const responseData = response.data as any;
          const recordingsData = Array.isArray(responseData) ? responseData : responseData.data || [];
          const channelId = selectedChannelIds[index];
          const channelNumber = channels.find(ch => ch.id === channelId)?.channelNumber ?? channelId;
          return acc.concat(
            recordingsData.map((recording: any) => ({
              channelId,
              channelNumber,
              startedAt: normalizeHatTimestamp(recording.startTime, deviceOffsetMinutes),
              endedAt: normalizeHatTimestamp(recording.endTime, deviceOffsetMinutes),
              fileSize: recording.fileSize,
              storeType: recording.storeType,
            }))
          );
        }, [])
        // Ordena por câmera e, dentro de cada câmera, por horário de início.
        .sort((a, b) => a.channelNumber - b.channelNumber || new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime());

      setRecordings(combined);
      if (combined.length) {
        setShowRecordingHint(true);
        setSidebarMode('recordings');
      } else {
        setError('Nenhuma gravação encontrada no período selecionado.');
      }
    } catch (requestError) {
      setRecordings([]);
      setError(messageFromError(requestError, 'Não foi possível consultar as gravações de uma ou mais câmeras.'));
    } finally {
      setLoadingRecordings(false);
    }
  };

  const startPlayback = async (
    recording: CameraRecording,
    requestedStartedAt = recording.startedAt,
    requestedQuality: HatJsVideoQuality = quality,
    requestedChannelIds: number[] = [recording.channelId],
    requestedTargetRecordings: CameraRecording[] | null = null
  ): Promise<boolean> => {
    if (!device || device.timeZone == null) {
      setError('O fuso horário (timeZone) deste equipamento não foi definido. Configure o timeZone do dispositivo para usar o playback.');
      return false;
    }

    const effectiveChannelIds = requestedChannelIds;

    const requestedTimeMs = new Date(requestedStartedAt).getTime();
    const targetTimeMs = Number.isFinite(requestedTimeMs) ? requestedTimeMs : new Date(recording.startedAt).getTime();

    const seekableRecordings = timelineRecordings;
    const targetRecordings = requestedTargetRecordings?.length
      ? requestedTargetRecordings
      : effectiveChannelIds
          .map(channelId => {
            const match = seekableRecordings.find(candidate => {
              if (candidate.channelId !== channelId) return false;
              const startMs = new Date(candidate.startedAt).getTime();
              const endMs = new Date(candidate.endedAt).getTime();
              return Number.isFinite(startMs) && Number.isFinite(endMs) && targetTimeMs >= startMs && targetTimeMs <= endMs;
            });
            return match || (recording.channelId === channelId ? recording : null);
          })
          .filter((r): r is CameraRecording => r !== null);

    if (!targetRecordings.length) {
      setError('Nenhuma câmera possui gravação no horário escolhido.');
      return false;
    }

    setError(null);
    setActiveRecording(recording);
    const requestedTime = new Date(requestedStartedAt);
    setCurrentPlaybackTime(Number.isNaN(requestedTime.getTime()) ? null : requestedTime);
    setLoadingPlayback(true);

    try {
      const activeTargets = targetRecordings.filter(target => {
        const targetEndMs = new Date(target.endedAt).getTime();
        return !Number.isFinite(targetTimeMs) || !Number.isFinite(targetEndMs) || targetEndMs >= targetTimeMs;
      });

      const activeResponses = await Promise.all(
        activeTargets.map(async target => {
          const targetStartMs = new Date(target.startedAt).getTime();
          const targetEndMs = new Date(target.endedAt).getTime();
          // Início efetivo do pedido: o instante global, mas sempre DENTRO do intervalo do próprio
          // trecho (nunca antes do início nem depois do fim — o backend rejeita startedAt >= endedAt).
          // Assim canais cujo trecho começa depois (upcoming) são pedidos do próprio início, e canais
          // cujo trecho já terminou (previous) não estouram o backend.
          let effectiveStartMs = target.startedAt ? targetStartMs : targetTimeMs;
          if (Number.isFinite(targetTimeMs) && targetTimeMs > targetStartMs) {
            effectiveStartMs = Math.min(targetTimeMs, targetEndMs - 1000);
          }
          const effectiveStart = Number.isFinite(effectiveStartMs) ? new Date(effectiveStartMs).toISOString() : target.startedAt;

          const payload = {
            deviceCode: device?.terminalID,
            startedAt: effectiveStart,
            endedAt: target.endedAt,
            fileSize: target.fileSize,
            storeType: target.storeType,
            timeZoneOffsetMinutes: device.timeZone * 60,
            quality: requestedQuality,
          };

          const response = await axios.post<CameraPlaybackStream>(`api/cameras/channels/${target.channelId}/playback`, payload, {
            params: {
              quality: requestedQuality,
            },
          });

          return response.data;
        })
      );
      const activeResponseByChannel = new Map(activeResponses.map(response => [response.channelId, response]));
      const responses: CameraPlaybackStream[] = targetRecordings.map(target => {
        const activeResponse = activeResponseByChannel.get(target.channelId);
        if (activeResponse) return activeResponse;

        const currentStream = playback?.find(stream => stream.channelId === target.channelId);
        return {
          channelId: target.channelId,
          url: currentStream?.url ?? '',
          contentType: currentStream?.contentType ?? '',
          startedAt: requestedStartedAt,
          endedAt: target.endedAt,
          inactive: true,
        };
      });

      // Mapa canal → intervalo REAL da gravação escolhida, para o player saber quando cada canal
      // entra (sincronização por relógio global) e quando está fora do trecho gravado (overlay).
      const targetByChannel = new Map(targetRecordings.map(target => [target.channelId, target]));

      const nextStreams = responses.map(response => {
        const channel = channels.find(ch => ch.id === response.channelId);
        const target = targetByChannel.get(response.channelId);
        return {
          channelId: response.channelId,
          title: channel ? channelLabel(channel) : `Câmera ${response.channelId}`,
          url: response.url,
          // Hora de parede do dispositivo para o player HATJS (evita conversão UTC no player).
          playback: {
            startedAt: toDeviceWallClockSeconds(response.startedAt, deviceOffsetMinutes),
            endedAt: toDeviceWallClockSeconds(response.endedAt, deviceOffsetMinutes),
            inactive: response.inactive,
            ...(target && {
              recordingStartedAt: toDeviceWallClockSeconds(target.startedAt, deviceOffsetMinutes),
              recordingEndedAt: toDeviceWallClockSeconds(target.endedAt, deviceOffsetMinutes),
            }),
          },
        };
      });

      // Anexa o intervalo real da gravação a cada stream, para o render reconstruir os streams do
      // player com os dados de sincronização/overlay (o endpoint de playback não os retorna).
      const enrichedResponses: CameraPlaybackStream[] = responses.map(response => {
        const target = targetByChannel.get(response.channelId);
        return target ? { ...response, recordingStartedAt: target.startedAt, recordingEndedAt: target.endedAt } : response;
      });

      const currentIds = (playback || []).map(s => s.channelId);
      const nextIds = enrichedResponses.map(r => r.channelId);
      const sameLayout = currentIds.length === nextIds.length && currentIds.every((id, i) => id === nextIds[i]);

      if (playback && playbackPlayerRef.current && sameLayout) {
        const restarted = await playbackPlayerRef.current.restartPlayback(nextStreams);
        if (!restarted) setPlayback(enrichedResponses);
      } else {
        setPlayback(enrichedResponses);
      }
      return true;
    } catch (requestError) {
      setPlayback(null);
      setActiveRecording(null);
      setCurrentPlaybackTime(null);
      setError(messageFromError(requestError, 'Não foi possível iniciar o playback de uma ou mais câmeras.'));
      return false;
    } finally {
      setLoadingPlayback(false);
    }
  };

  const selectTimelinePoint = async (recording: CameraRecording, timestamp: Date) => {
    const timeMs = timestamp.getTime();

    const seekableRecordings = timelineRecordings;
    const startMsOf = (rec: CameraRecording) => new Date(rec.startedAt).getTime();
    const endMsOf = (rec: CameraRecording) => new Date(rec.endedAt).getTime();
    const coversInstant = (rec: CameraRecording) => timeMs >= startMsOf(rec) && timeMs <= endMsOf(rec);

    // O LAYOUT é fixo durante a sessão: são todos os canais que têm alguma gravação exibida na
    // timeline (o mesmo conjunto do play). Nunca removemos/readicionamos quadrantes — isso evita o
    // colapso do layout. O clique só muda O QUE cada quadrante mostra.
    const layoutChannelIds = playback?.length ? playback.map(stream => stream.channelId) : Array.from(new Set(seekableRecordings.map(rec => rec.channelId)));

    // Para cada canal do layout escolhe, SOMENTE entre as gravações da timeline daquele canal:
    //  1) a que cobre o instante; senão
    //  2) a próxima à frente (canal reentra pausado/"sem imagem" até o relógio alcançá-la); senão
    //  3) a última anterior (canal fica no layout como "sem imagem", pois o trecho já terminou).
    const perChannelTargets = layoutChannelIds
      .map(channelId => {
        const channelRecordings = seekableRecordings.filter(rec => rec.channelId === channelId);
        const covering = channelRecordings.find(coversInstant);
        if (covering) return covering;
        const upcoming = channelRecordings.filter(rec => startMsOf(rec) >= timeMs).sort((a, b) => startMsOf(a) - startMsOf(b))[0];
        if (upcoming) return upcoming;
        const previous = channelRecordings.filter(rec => endMsOf(rec) < timeMs).sort((a, b) => endMsOf(b) - endMsOf(a))[0];
        return previous ?? null;
      })
      .filter((rec): rec is CameraRecording => rec !== null);

    if (!perChannelTargets.length) {
      setError('Nenhuma câmera selecionada possui gravação no horário escolhido.');
      return;
    }

    // Âncora: o canal que cobre o instante (preferindo o que o clique tocou); senão o primeiro alvo.
    const anchor =
      (coversInstant(recording) && perChannelTargets.find(t => t.channelId === recording.channelId)) ||
      perChannelTargets.find(coversInstant) ||
      perChannelTargets.find(t => t.channelId === recording.channelId) ||
      perChannelTargets[0];

    // Mantém o instante dentro da âncora e nunca colado no fim (o backend rejeita startedAt >= endedAt).
    const anchorStartMs = new Date(anchor.startedAt).getTime();
    const anchorEndMs = new Date(anchor.endedAt).getTime();
    const clampedMs = Math.min(Math.max(timeMs, anchorStartMs), anchorEndMs - 1000);
    const safeTimestamp = new Date(Number.isFinite(clampedMs) ? clampedMs : anchorStartMs);

    await startPlayback(
      anchor,
      safeTimestamp.toISOString(),
      quality,
      perChannelTargets.map(target => target.channelId),
      perChannelTargets
    );
  };

  const playSelectedRecordings = async () => {
    const recordingsToPlay = recordings.filter(recording => selectedRecordingKeys.includes(recordingKey(recording)));
    if (!recordingsToPlay.length || loadingPlayback) return;

    // O player abre um quadrante por câmera. Quando há vários trechos selecionados da mesma
    // câmera, inicia pelo mais antigo de cada uma; os demais ficam navegáveis pela timeline.
    const initialByChannel = new Map<number, CameraRecording>();
    recordingsToPlay.forEach(recording => {
      const existing = initialByChannel.get(recording.channelId);
      if (!existing || new Date(recording.startedAt).getTime() < new Date(existing.startedAt).getTime()) {
        initialByChannel.set(recording.channelId, recording);
      }
    });
    const initialRecordings = Array.from(initialByChannel.values());

    // Âncora = gravação que começa mais cedo. Assim o instante inicial global é o menor startedAt e
    // nenhum canal é pedido "adiantado" (senão perderíamos o trecho inicial de quem começa antes).
    const anchorRecording = initialRecordings.reduce((earliest, current) =>
      new Date(current.startedAt).getTime() < new Date(earliest.startedAt).getTime() ? current : earliest
    );
    await startPlayback(
      anchorRecording,
      anchorRecording.startedAt,
      quality,
      initialRecordings.map(recording => recording.channelId),
      initialRecordings
    );
  };

  const stopPlayback = () => {
    setPlayback(null);
    setActiveRecording(null);
    setCurrentPlaybackTime(null);
    setLoadingPlayback(false);
    setError(null);
  };

  const changeQuality = () => undefined;

  const downloadFile = async (downloadUrl: string, generation: number) => {
    setDownloadPhase('transferring');
    const cancelSource = axios.CancelToken.source();
    activeFileDownloadCancelRef.current = cancelSource;
    try {
      const response = await axios.get<Blob>(downloadUrl, { responseType: 'blob', cancelToken: cancelSource.token });
      if (generation !== downloadGenerationRef.current) return;
      const contentDisposition = String(response.headers['content-disposition'] || '');
      const filenameMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition);
      const filename = filenameMatch?.[1]?.trim() || 'camera-recording.mp4';
      const objectUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } finally {
      if (activeFileDownloadCancelRef.current === cancelSource) activeFileDownloadCancelRef.current = null;
    }
  };

  const delay = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));

  // Baixa UMA gravação: cria a tarefa e, se necessário, faz polling até concluir. Resolve com
  // `true` em sucesso e `false` em falha/timeout (sem lançar), para a fila continuar nas demais.
  const downloadOneRecording = async (recording: CameraRecording, generation: number): Promise<boolean> => {
    try {
      const response = await axios.post<CameraDownloadTask>(
        `api/cameras/channels/${recording.channelId}/downloads`,
        {
          deviceCode: device?.terminalID,
          productKey: device?.productKey,
          startedAt: recording.startedAt,
          endedAt: recording.endedAt,
          fileSize: recording.fileSize,
          storeType: recording.storeType,
          timeZoneOffsetMinutes: device ? device.timeZone * 60 : undefined,
        },
        { params: { streamType: 0 } }
      );
      if (generation !== downloadGenerationRef.current) return false;

      const task = response.data;
      // Download direto (sem tarefa assíncrona).
      if (task.downloadUrl) {
        await downloadFile(task.downloadUrl, generation);
        return true;
      }
      if (!task.taskId) {
        setError('Não foi possível iniciar a exportação.');
        return false;
      }

      // Polling da tarefa até concluir. Registra o taskId ativo para permitir cancelamento.
      const taskId = task.taskId;
      activeDownloadTaskIdRef.current = taskId;
      for (let attempt = 0; attempt < DOWNLOAD_MAX_ATTEMPTS; attempt += 1) {
        await delay(DOWNLOAD_POLL_INTERVAL_MS);
        if (generation !== downloadGenerationRef.current) return false;
        const statusResponse = await axios.get<CameraDownloadTask>(`api/cameras/downloads/${taskId}`);
        if (generation !== downloadGenerationRef.current) return false;

        const statusTask = statusResponse.data;
        const status = statusTask.status?.toUpperCase() || 'UNKNOWN';
        if (statusTask.downloadUrl && (statusTask.source === 'SDK_DOWNLOAD_TASK' || status === 'COMPLETED')) {
          await downloadFile(statusTask.downloadUrl, generation);
          return true;
        }
        if (status === 'FAILED' || status === 'ERROR') {
          setError('A exportação falhou. Tente novamente.');
          return false;
        }
      }
      setError('O servidor ainda está processando o vídeo. Aguarde alguns minutos e tente consultar novamente.');
      return false;
    } catch (requestError) {
      if (generation === downloadGenerationRef.current) setError(messageFromError(requestError, 'Não foi possível iniciar a exportação.'));
      return false;
    } finally {
      activeDownloadTaskIdRef.current = null;
    }
  };

  // Baixa uma ou mais gravações EM SEQUÊNCIA (uma por vez), reaproveitando a máquina de tarefa.
  // O card de cada gravação mostra o spinner enquanto ela estiver na fila (downloadingKeys).
  const startDownloads = async (recordingsToDownload: CameraRecording[]) => {
    if (!recordingsToDownload.length || downloadingKeys.length) return;
    setError(null);
    setDownloadNotice(null);
    downloadGenerationRef.current += 1;
    const generation = downloadGenerationRef.current;

    const keys = recordingsToDownload.map(recordingKey);
    setDownloadingKeys(keys);
    setDownloadQueueTotal(keys.length);
    setDownloadQueueIndex(1);
    let completed = 0;
    try {
      for (const [index, recording] of recordingsToDownload.entries()) {
        if (generation !== downloadGenerationRef.current) return;
        setDownloadQueueIndex(index + 1);
        setDownloadPhase('preparing');
        // Sequencial de propósito: uma exportação por vez reaproveita a máquina de tarefa/polling.
        const succeeded = await downloadOneRecording(recording, generation);
        if (generation !== downloadGenerationRef.current) return;
        if (succeeded) completed += 1;
        // Remove esta gravação da fila (o card volta ao ícone de download).
        if (generation === downloadGenerationRef.current) {
          setDownloadingKeys(current => current.filter(key => key !== recordingKey(recording)));
        }
      }
      if (completed > 0) {
        setDownloadNotice(
          completed === 1
            ? 'Gravação transferida. Verifique os downloads do navegador.'
            : `${completed} gravações transferidas. Verifique os downloads do navegador.`
        );
      }
    } finally {
      if (generation === downloadGenerationRef.current) setDownloadingKeys([]);
    }
  };

  // Cancela a exportação em andamento: invalida a geração (para o polling/fila) e pede à HAT para
  // excluir a tarefa atual. Limpa o estado imediatamente para a UI responder na hora.
  const cancelDownloads = () => {
    // Invalida a geração: o laço de polling/fila para na próxima checagem.
    downloadGenerationRef.current += 1;
    const taskId = activeDownloadTaskIdRef.current;
    activeDownloadTaskIdRef.current = null;
    activeFileDownloadCancelRef.current?.cancel('Exportação cancelada');
    activeFileDownloadCancelRef.current = null;
    setDownloadingKeys([]);
    setDownloadNotice(null);
    // Cancelamento é best-effort: se a HAT falhar, a tarefa expira sozinha. Não bloqueia a UI.
    if (taskId) void axios.delete(`api/cameras/downloads/${taskId}`).catch(() => undefined);
  };

  const isDownloading = downloadingKeys.length > 0;

  const startSelectedDownloads = () => void startDownloads(recordings.filter(rec => selectedRecordingKeys.includes(recordingKey(rec))));

  const onVideoDisabled = theme.palette.text.disabled;
  const onVideoPrimary = theme.palette.text.primary;
  const onVideoSecondary = theme.palette.text.secondary;
  const isPlaybackActive = Boolean(playback?.length);
  const playbackControlsLocked = isPlaybackActive || loadingPlayback;
  const canSearch = Boolean(vehicle) && selectedChannelIds.length > 0 && !loadingRecordings;
  const channelLabelsByNumber = channels.reduce<Record<number, string>>((labels, channel) => {
    labels[channel.channelNumber] = channelLabel(channel);
    return labels;
  }, {});

  // Agrupa as gravações por câmera para exibir seções na lista. `recordings` já vem ordenado por
  // câmera e horário, então basta quebrar em grupos quando o channelNumber muda.
  const recordingGroups = (() => {
    const groups: Array<{ channelNumber: number; title: string; items: CameraRecording[] }> = [];
    recordings.forEach(recording => {
      const last = groups[groups.length - 1];
      if (last && last.channelNumber === recording.channelNumber) {
        last.items.push(recording);
        return;
      }
      const channel = channels.find(ch => ch.id === recording.channelId);
      groups.push({
        channelNumber: recording.channelNumber,
        title: channel ? channelLabel(channel) : `Câmera ${recording.channelNumber}`,
        items: [recording],
      });
    });
    return groups;
  })();

  useEffect(() => {
    if (!recordings.length) {
      setExpandedRecordingGroups({});
      return;
    }
    const shouldStartExpanded = recordings.length <= 10;
    setExpandedRecordingGroups(
      recordingGroups.reduce<Record<number, boolean>>((expanded, group) => {
        expanded[group.channelNumber] = shouldStartExpanded;
        return expanded;
      }, {})
    );
  }, [recordings]);

  // Gravações exibidas na timeline. Enquanto há playback ativo, mostra estritamente os trechos
  // em reprodução (o play individual não deve herdar seleções residuais). Sem playback, usa a
  // seleção da lista para pré-visualizar o que será reproduzido.
  const timelineRecordings = (() => {
    // Se há gravações selecionadas na lista, a timeline mostra todas elas — mesmo que o player
    // comece tocando apenas um trecho por câmera. Assim você navega entre os trechos escolhidos.
    const selectedForTimeline = recordings.filter(recording => selectedRecordingKeys.includes(recordingKey(recording)));
    if (selectedForTimeline.length) return selectedForTimeline;

    // Sem seleção (ex.: play individual pelo card): mostra estritamente o que está em reprodução.
    if (playback?.length) {
      const fromPlayback = playback
        .map(stream => {
          const streamStartMs = new Date(stream.startedAt).getTime();
          const streamEndMs = new Date(stream.endedAt).getTime();
          return (
            recordings.find(candidate => {
              if (candidate.channelId !== stream.channelId) return false;
              const candidateStartMs = new Date(candidate.startedAt).getTime();
              const candidateEndMs = new Date(candidate.endedAt).getTime();
              if (!Number.isFinite(candidateStartMs) || !Number.isFinite(candidateEndMs)) return false;
              // Interseção de intervalos: tolera diferenças de timezone/arredondamento entre o
              // endpoint de gravações e o de playback.
              return (
                (Number.isFinite(streamStartMs) ? streamStartMs : candidateStartMs) <= candidateEndMs &&
                (Number.isFinite(streamEndMs) ? streamEndMs : candidateEndMs) >= candidateStartMs
              );
            }) ?? null
          );
        })
        .filter((recording): recording is CameraRecording => recording !== null);
      if (fromPlayback.length) return fromPlayback;
      return activeRecording ? [activeRecording] : [];
    }

    return [];
  })();

  // Eixo da timeline: quando há trechos em reprodução, o eixo cobre exatamente o intervalo
  // dessas gravações (não o período inteiro do filtro), evitando eixo longo e vazio e o
  // desalinhamento de fuso entre o filtro (local) e as gravações (UTC).
  const timelineRange = (() => {
    if (timelineRecordings.length) {
      const startMs = Math.min(...timelineRecordings.map(recording => new Date(recording.startedAt).getTime()));
      const endMs = Math.max(...timelineRecordings.map(recording => new Date(recording.endedAt).getTime()));
      if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
        return { start: new Date(startMs), end: new Date(endMs) };
      }
    }
    return { start: new Date(startedAt), end: new Date(endedAt) };
  })();

  useEffect(() => {
    if (!playback?.length || !currentPlaybackTime || loadingPlayback || !timelineRecordings.length) return;

    const currentMs = currentPlaybackTime.getTime();
    if (!Number.isFinite(currentMs)) return;

    const startMsOf = (recording: CameraRecording) => new Date(recording.startedAt).getTime();
    const endMsOf = (recording: CameraRecording) => new Date(recording.endedAt).getTime();
    const activeAtCurrentTime = timelineRecordings.filter(recording => {
      const recordingStartMs = startMsOf(recording);
      const recordingEndMs = endMsOf(recording);
      return Number.isFinite(recordingStartMs) && Number.isFinite(recordingEndMs) && currentMs >= recordingStartMs && currentMs <= recordingEndMs + 1000;
    });

    if (!activeAtCurrentTime.length) {
      autoAdvancePlaybackKeyRef.current = null;
      return;
    }

    const activeEndMs = Math.max(...activeAtCurrentTime.map(endMsOf).filter(Number.isFinite));
    if (!Number.isFinite(activeEndMs) || currentMs < activeEndMs - 2000) {
      autoAdvancePlaybackKeyRef.current = null;
      return;
    }

    const nextRecording = timelineRecordings
      .filter(recording => {
        const recordingStartMs = startMsOf(recording);
        return Number.isFinite(recordingStartMs) && recordingStartMs > activeEndMs + 1000;
      })
      .sort((a, b) => startMsOf(a) - startMsOf(b))[0];

    if (!nextRecording) return;

    const nextStartMs = startMsOf(nextRecording);
    const autoAdvanceKey = `${nextRecording.channelId}:${nextRecording.startedAt}:${nextRecording.endedAt}`;
    if (autoAdvancePlaybackKeyRef.current === autoAdvanceKey) return;

    autoAdvancePlaybackKeyRef.current = autoAdvanceKey;
    void selectTimelinePoint(nextRecording, new Date(nextStartMs));
  }, [currentPlaybackTime, loadingPlayback, playback, timelineRecordings]);

  const searchHint = (() => {
    if (loadingRecordings) return null;
    if (isAdmin && !company?.id && !fixedTruckImplementId) return 'Selecione uma empresa para listar os veículos.';
    if (!vehicle) return 'Selecione um veículo para listar as câmeras.';
    if (loadingChannels) return 'Carregando as câmeras do veículo...';
    if (!channels.length) return 'Nenhuma câmera disponível para este veículo.';
    if (!selectedChannelIds.length) return 'Selecione ao menos uma câmera.';
    return null;
  })();

  const customPeriodDurationMs = customStartedAt && customEndedAt ? dayjs(customEndedAt).diff(dayjs(customStartedAt), 'millisecond') : null;
  const customPeriodTooLong = customPeriodDurationMs !== null && customPeriodDurationMs > MAX_CUSTOM_PERIOD_MS;
  const customPeriodInvalid = customPeriodDurationMs !== null && customPeriodDurationMs <= 0;
  const canApplyCustomPeriod = Boolean(customStartedAt && customEndedAt) && !customPeriodTooLong && !customPeriodInvalid;

  const selectPeriodPreset = (hours: number) => {
    const preset = presetPeriod(hours, deviceOffsetMinutes);
    setStartedAt(preset.startedAt);
    setEndedAt(preset.endedAt);
    setSelectedPresetHours(hours);
    setError(null);
  };

  const openCustomPeriodDialog = () => {
    setCustomStartedAt(selectedPresetHours === null ? startedAt : null);
    setCustomEndedAt(selectedPresetHours === null ? endedAt : null);
    setCustomPeriodOpen(true);
  };

  const updateCustomStartedAt = (value: Dayjs | null) => {
    if (!value) return;
    setCustomStartedAt(value.format('YYYY-MM-DDTHH:mm'));
  };

  const updateCustomEndedAt = (value: Dayjs | null) => {
    if (!value) return;
    setCustomEndedAt(value.format('YYYY-MM-DDTHH:mm'));
  };

  const applyCustomPeriod = () => {
    if (!canApplyCustomPeriod || !customStartedAt || !customEndedAt) return;
    setStartedAt(customStartedAt);
    setEndedAt(customEndedAt);
    setSelectedPresetHours(null);
    setCustomPeriodOpen(false);
    setError(null);
  };

  const togglePlayerFullscreen = () => {
    const playerElement = playerContainerRef.current;
    if (!playerElement) return;

    if (!isPlayerFullscreen) {
      if (playerElement.requestFullscreen) {
        void playerElement.requestFullscreen().catch(() => {
          // Fallback: apenas muda o estado visual se fullscreen API não funcionar
          setIsPlayerFullscreen(true);
        });
      } else if ((playerElement as any).webkitRequestFullscreen) {
        void (playerElement as any).webkitRequestFullscreen().catch(() => {
          setIsPlayerFullscreen(true);
        });
      } else {
        setIsPlayerFullscreen(true);
      }
    } else {
      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else if ((document as any).webkitFullscreenElement) {
        void (document as any).webkitExitFullscreen();
      }
      setIsPlayerFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = Boolean(document.fullscreenElement || (document as any).webkitFullscreenElement);
      setIsPlayerFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const ReplayIcon = ({ size = 24, color = 'currentColor', className = '', ...props }) => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        <path d="M9.5 8.5v7a1 1 0 0 0 1.5.85l5.5-3.5a1 1 0 0 0 0-1.7l-5.5-3.5a1 1 0 0 0-1.5.85Z" />
        <path d="M11 20.5A8.5 8.5 0 1 0 4 9" />
        <path d="M4 3v6h6" />
        <path d="M9.1 20.0A8.5 8.5 0 0 1 7.1 19.0" />
        <path d="M5.0 16.8A8.5 8.5 0 0 1 4.0 14.9" />
      </svg>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: 'primary.main',
        borderRadius: '8px',
        padding: '24px',
        margin: '24px',
      }}
    >
      <Box
        ref={monitorLayoutRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '360px minmax(0, 1fr)' },
          gap: 1.5,
          alignItems: 'stretch',
        }}
      >
        {(() => (
          <Box
            ref={formRef}
            component="aside"
            aria-label="Controles do playback"
            sx={{
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              height: { xs: 'auto', md: '72vh' },
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                px: SIDEBAR_PADDING,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <VideoLibraryRounded
                  sx={{
                    fontSize: 20,
                    flexShrink: 0,
                    position: 'relative',
                    bottom: 1,
                  }}
                  color="info"
                />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: 'text.primary',
                    m: 0,
                  }}
                >
                  Playback
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const query = buildCameraNavigationQuery();
                  history.push(query ? `/live-monitor?${query}` : liveMonitorRoute);
                }}
                sx={{
                  padding: '4px 12px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderRadius: '8px',
                  borderColor: theme.palette.divider,
                  backgroundColor: 'background.default',
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                    borderColor: theme.palette.secondary.main,
                    color: 'secondary.main',
                  },
                }}
              >
                Ir para Ao Vivo
              </Button>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                px: SIDEBAR_PADDING,
                py: 1.5,
              }}
            >
              {error && (
                <Alert severity="error" variant="outlined" onClose={() => setError(null)} sx={{ borderRadius: '8px', mb: 1.5 }}>
                  {error}
                </Alert>
              )}

              {isDownloading && (
                <Box
                  role="status"
                  aria-live="polite"
                  aria-label="Andamento da exportação"
                  sx={{
                    mb: 1,
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    backgroundColor: alpha(theme.palette.info.main, 0.045),
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        color: 'info.main',
                        backgroundColor: alpha(theme.palette.info.main, 0.12),
                      }}
                    >
                      <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}>
                        {downloadPhase === 'preparing' ? 'Preparando download' : 'Baixando arquivo'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35 }}>
                        {downloadQueueTotal > 1 && `Gravação ${downloadQueueIndex} de ${downloadQueueTotal} · `}
                        {downloadPhase === 'preparing' ? 'Pode levar alguns minutos' : 'Transferindo para o navegador'}
                      </Typography>
                    </Box>
                    <Tooltip title="Cancelar exportação">
                      <IconButton
                        size="small"
                        aria-label="Cancelar exportação"
                        onClick={cancelDownloads}
                        sx={{ mt: -0.25, mr: -0.25, color: 'error.main', '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.08) } }}
                      >
                        <CloseRoundedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <LinearProgress color="secondary" aria-label="Exportação em andamento" sx={{ height: 4, borderRadius: 2 }} />
                </Box>
              )}

              {sidebarMode === 'filters' ? (
                <Stack spacing={1.25} divider={<Box sx={{ borderTop: '1px solid', borderColor: alpha(theme.palette.text.primary, 0.06) }} />}>
                  <Stack spacing={1.25}>
                    {isAdmin && (
                      <Box>
                        <SectionLabel title="Empresa" />
                        <SelectPaginate
                          apiUrl="api/admin/camera-devices/companies/options"
                          value={company}
                          valueName="name"
                          searchName="search"
                          placeholder="Buscar por empresa"
                          menuPortalTarget={document.body}
                          controlHeight={36}
                          controlFontSize="0.875rem"
                          isRequired
                          displayFormat={option => option?.name || String(option?.id || '')}
                          onChange={value => {
                            setCompany(value as CompanyOption | null);
                            setVehicle(null);
                          }}
                        />
                      </Box>
                    )}
                    {
                      <Box>
                        <SectionLabel title="Veículo" />
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 36px', columnGap: 0.75, alignItems: 'center' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <SelectPaginateFromPaginatedApi
                              apiUrl={vehicleApiUrl}
                              value={vehicle}
                              valueName="licensePlate"
                              searchName="searchLicensePlate"
                              placeholder="Buscar por placa"
                              menuPortalTarget={document.body}
                              controlHeight={36}
                              controlFontSize="0.875rem"
                              isRequired
                              isDisabled={isAdmin && !company?.id && !fixedTruckImplementId}
                              displayFormat={vehicleOptionLabel}
                              renderOption={option => renderVehicleOption(option, theme)}
                              minWidth="0"
                              desktopMinWidth="0"
                              onChange={value => setVehicle(value as VehicleOption | null)}
                            />
                          </Box>
                          <Tooltip title="Atualizar veículos e status">
                            <span>
                              <IconButton
                                size="small"
                                color="secondary"
                                disabled={(isAdmin && !company?.id && !fixedTruckImplementId) || refreshingVehicleList}
                                onClick={refreshVehicleList}
                                sx={{ border: '1px solid', borderColor: alpha(theme.palette.text.primary, 0.18), borderRadius: '8px', height: 36, width: 36 }}
                              >
                                {refreshingVehicleList ? <CircularProgress color="secondary" size={16} /> : <SyncRoundedIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Box>
                    }
                  </Stack>

                  <Box sx={compactSidebarSectionSx}>
                    <SectionLabel title="Câmeras" />

                    {loadingChannels ? (
                      <Stack alignItems="center" spacing={0.75} sx={{ py: 2 }}>
                        <CircularProgress size={20} color="secondary" />
                        <Typography variant="caption" color="text.secondary">
                          Carregando câmeras
                        </Typography>
                      </Stack>
                    ) : channels.length > 0 ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                        {channels.map(channel => {
                          const isSelected = selectedChannelIds.includes(channel.id);
                          return (
                            <ButtonBase
                              key={channel.id}
                              onClick={() => toggleChannel(channel.id)}
                              aria-pressed={isSelected}
                              aria-label={channelLabel(channel)}
                              sx={{
                                height: 32,
                                minHeight: 32,
                                px: 1,
                                gap: 0.625,
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: isSelected ? alpha(theme.palette.secondary.main, 0.4) : alpha(theme.palette.text.primary, 0.12),
                                backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.08) : 'background.default',
                                color: isSelected ? 'text.primary' : 'text.secondary',
                                transition: theme.transitions.create(['background-color', 'border-color', 'color']),
                                '&:hover': {
                                  borderColor: isSelected ? alpha(theme.palette.secondary.main, 0.6) : alpha(theme.palette.text.primary, 0.28),
                                  backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.12) : alpha(theme.palette.text.primary, 0.04),
                                },
                                '&.Mui-focusVisible': {
                                  outline: `2px solid ${theme.palette.secondary.main}`,
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              {isSelected ? (
                                <VideocamRoundedIcon sx={{ fontSize: 17, color: 'secondary.main', flexShrink: 0 }} />
                              ) : (
                                <VideocamOutlinedIcon sx={{ fontSize: 17, color: 'text.disabled', flexShrink: 0 }} />
                              )}
                              <Typography variant="body2" fontWeight={isSelected ? 600 : 500} color="inherit" noWrap sx={{ fontSize: '0.8125rem' }}>
                                {channelLabel(channel)}
                              </Typography>
                            </ButtonBase>
                          );
                        })}
                      </Box>
                    ) : (
                      <Stack
                        alignItems="center"
                        spacing={0.75}
                        sx={{
                          py: 2.5,
                          px: 1.5,
                          borderRadius: '10px',
                          backgroundColor: alpha(theme.palette.text.primary, 0.03),
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {isAdmin && !company?.id && !fixedTruckImplementId
                            ? 'Selecione uma empresa para listar os veículos.'
                            : vehicle
                            ? 'Não há câmeras ativas para este veículo.'
                            : 'Selecione um veículo para listar as câmeras.'}
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  {channels.length > 0 && (
                    <Box sx={compactSidebarSectionSx}>
                      <SectionLabel title="Período" />
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <Stack spacing={0.75}>
                          <Stack direction="row" spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75 }}>
                              {[
                                { label: '1h', hours: 1 },
                                { label: '24h', hours: 24 },
                              ].map(preset => (
                                <ButtonBase
                                  key={preset.label}
                                  onClick={() => selectPeriodPreset(preset.hours)}
                                  sx={{
                                    minHeight: 32,
                                    borderRadius: '8px',
                                    border: '1px solid',
                                    borderColor:
                                      selectedPresetHours === preset.hours ? alpha(theme.palette.secondary.main, 0.4) : alpha(theme.palette.text.primary, 0.12),
                                    backgroundColor: selectedPresetHours === preset.hours ? alpha(theme.palette.secondary.main, 0.08) : 'background.default',
                                    color: selectedPresetHours === preset.hours ? 'secondary.main' : 'text.secondary',
                                    fontSize: '0.75rem',
                                    fontWeight: selectedPresetHours === preset.hours ? 600 : 500,
                                    transition: theme.transitions.create(['background-color', 'border-color', 'color']),
                                    '&:hover': {
                                      borderColor:
                                        selectedPresetHours === preset.hours
                                          ? alpha(theme.palette.secondary.main, 0.6)
                                          : alpha(theme.palette.text.primary, 0.28),
                                      backgroundColor:
                                        selectedPresetHours === preset.hours
                                          ? alpha(theme.palette.secondary.main, 0.12)
                                          : alpha(theme.palette.text.primary, 0.04),
                                    },
                                    '&.Mui-focusVisible': {
                                      outline: `2px solid ${theme.palette.secondary.main}`,
                                      outlineOffset: 2,
                                    },
                                  }}
                                >
                                  {preset.label}
                                </ButtonBase>
                              ))}
                            </Box>
                            <ButtonBase
                              onClick={openCustomPeriodDialog}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                minHeight: 32,
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: selectedPresetHours === null ? alpha(theme.palette.secondary.main, 0.4) : alpha(theme.palette.text.primary, 0.12),
                                backgroundColor: selectedPresetHours === null ? alpha(theme.palette.secondary.main, 0.08) : 'background.default',
                                color: selectedPresetHours === null ? 'secondary.main' : 'text.secondary',
                                fontSize: '0.75rem',
                                fontWeight: selectedPresetHours === null ? 600 : 500,
                                transition: theme.transitions.create(['background-color', 'border-color', 'color']),
                                '&:hover': {
                                  borderColor:
                                    selectedPresetHours === null ? alpha(theme.palette.secondary.main, 0.6) : alpha(theme.palette.text.primary, 0.28),
                                  backgroundColor:
                                    selectedPresetHours === null ? alpha(theme.palette.secondary.main, 0.12) : alpha(theme.palette.text.primary, 0.04),
                                },
                                '&.Mui-focusVisible': {
                                  outline: `2px solid ${theme.palette.secondary.main}`,
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              Personalizado
                            </ButtonBase>
                          </Stack>
                          {selectedPresetHours === null && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1.3 }}>
                              {dayjs(startedAt).format('DD/MM/YY HH:mm')} → {dayjs(endedAt).format('DD/MM/YY HH:mm')}
                            </Typography>
                          )}
                        </Stack>
                        <Dialog open={customPeriodOpen} onClose={() => setCustomPeriodOpen(false)} maxWidth="xs" fullWidth>
                          <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Período personalizado</DialogTitle>
                          <DialogContent sx={{ pt: 1.5, overflow: 'visible' }}>
                            <Stack spacing={1.5}>
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                                  Início
                                </Typography>
                                <DateTimePicker
                                  value={customStartedAt ? dayjs(customStartedAt) : null}
                                  onChange={updateCustomStartedAt}
                                  inputFormat="DD/MM/YY HH:mm"
                                  disableFuture
                                  minDateTime={customEndedAt ? dayjs(customEndedAt).subtract(MAX_CUSTOM_PERIOD_MS, 'millisecond') : undefined}
                                  maxDateTime={customEndedAt ? dayjs(customEndedAt) : undefined}
                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      fullWidth
                                      size="small"
                                      label=""
                                      placeholder="Selecione o início"
                                      InputLabelProps={{ shrink: false }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                        '& .MuiInputBase-input': { minWidth: 0, fontSize: '0.875rem' },
                                      }}
                                    />
                                  )}
                                />
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                                  Fim
                                </Typography>
                                <DateTimePicker
                                  value={customEndedAt ? dayjs(customEndedAt) : null}
                                  onChange={updateCustomEndedAt}
                                  inputFormat="DD/MM/YY HH:mm"
                                  disableFuture
                                  minDateTime={customStartedAt ? dayjs(customStartedAt) : undefined}
                                  maxDateTime={customStartedAt ? dayjs(customStartedAt).add(MAX_CUSTOM_PERIOD_MS, 'millisecond') : undefined}
                                  renderInput={params => (
                                    <TextField
                                      {...params}
                                      fullWidth
                                      size="small"
                                      label=""
                                      placeholder="Selecione o fim"
                                      InputLabelProps={{ shrink: false }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                        '& .MuiInputBase-input': { minWidth: 0, fontSize: '0.875rem' },
                                      }}
                                    />
                                  )}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: customPeriodTooLong || customPeriodInvalid ? 'error.main' : 'text.secondary',
                                  lineHeight: 1.3,
                                }}
                              >
                                Intervalo permitido: até 24 horas.
                              </Typography>
                            </Stack>
                          </DialogContent>
                          <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button variant="outlined" color="secondary" onClick={() => setCustomPeriodOpen(false)} sx={{ textTransform: 'none' }}>
                              Cancelar
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={applyCustomPeriod}
                              disabled={!canApplyCustomPeriod}
                              sx={{ textTransform: 'none' }}
                            >
                              Aplicar
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </LocalizationProvider>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', gap: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
                    <ButtonBase
                      disabled={playbackControlsLocked}
                      onClick={() => {
                        setSidebarMode('filters');
                        setError(null);
                      }}
                      sx={{
                        minHeight: 30,
                        minWidth: 0,
                        px: 0.75,
                        gap: 0.375,
                        borderRadius: '8px',
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
                        '&.Mui-focusVisible': {
                          outline: `2px solid ${theme.palette.secondary.main}`,
                          outlineOffset: 1,
                        },
                        '&.Mui-disabled': { opacity: 0.45 },
                      }}
                    >
                      <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                      Voltar para filtros
                    </ButtonBase>
                    {(vehicle?.licensePlate || fixedLicensePlate) && (
                      <Chip
                        label={vehicle?.licensePlate || fixedLicensePlate}
                        size="small"
                        title={vehicle?.licensePlate || fixedLicensePlate}
                        sx={{
                          flexShrink: 1,
                          maxWidth: 155,
                          fontSize: '0.6875rem',
                          '& .MuiChip-label': {
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    )}
                  </Stack>

                  <Box
                    aria-live="polite"
                    sx={{
                      px: 0.5,
                      py: 0.75,
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: showRecordingHint ? 1.25 : 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.9375rem', lineHeight: 1.2 }}>
                        Gravações disponíveis{' '}
                        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8125rem', fontWeight: 500 }}>
                          ({recordings.length})
                        </Box>
                      </Typography>
                      <Tooltip title={showRecordingHint ? 'Ocultar informações' : 'Mostrar informações'}>
                        <IconButton
                          size="small"
                          aria-label={showRecordingHint ? 'Ocultar informações das gravações' : 'Mostrar informações das gravações'}
                          onClick={() => setShowRecordingHint(current => !current)}
                          sx={{
                            p: 0.375,
                            color: 'info.main',
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {showRecordingHint && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.75,
                          px: 1.25,
                          py: 0.875,
                          mb: 1.25,
                          borderRadius: '10px',
                          backgroundColor: alpha(theme.palette.info.main, 0.08),
                        }}
                      >
                        <InfoOutlinedIcon sx={{ mt: '1px', fontSize: 15, color: 'info.main', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1.5 }}>
                          {isPlaybackActive ? 'Pare a reprodução para alterar a seleção.' : 'Selecione as gravações e clique em reproduzir.'}
                        </Typography>
                      </Box>
                    )}

                    <List
                      disablePadding
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        pr: 0.25,
                      }}
                    >
                      {recordingGroups.map(group => {
                        const groupKeys = group.items.map(recordingKey);
                        const selectedCount = groupKeys.filter(key => selectedRecordingKeys.includes(key)).length;
                        const isGroupSelected = group.items.length > 0 && selectedCount === group.items.length;
                        const isGroupPartiallySelected = selectedCount > 0 && !isGroupSelected;
                        const isGroupExpanded = expandedRecordingGroups[group.channelNumber] ?? false;
                        return (
                          <React.Fragment key={`group-${group.channelNumber}`}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 0.5,
                                py: 0.5,
                                mb: isGroupExpanded ? 0.75 : 0,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                backgroundColor: 'background.paper',
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                setExpandedRecordingGroups(current => ({
                                  ...current,
                                  [group.channelNumber]: !isGroupExpanded,
                                }))
                              }
                            >
                              <ButtonBase
                                disabled={playbackControlsLocked || !group.items.length}
                                onClick={event => {
                                  event.stopPropagation();
                                  toggleRecordingGroupSelection(group.items);
                                }}
                                role="checkbox"
                                aria-checked={isGroupPartiallySelected ? 'mixed' : isGroupSelected}
                                aria-label={`${isGroupSelected ? 'Desmarcar' : 'Selecionar'} gravações de ${group.title}`}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 18,
                                  height: 18,
                                  flexShrink: 0,
                                  border: '1.5px solid',
                                  borderColor: isGroupSelected || isGroupPartiallySelected ? 'secondary.main' : alpha(theme.palette.text.primary, 0.25),
                                  borderRadius: '5px',
                                  color: 'secondary.contrastText',
                                  backgroundColor: isGroupSelected || isGroupPartiallySelected ? 'secondary.main' : 'transparent',
                                  transition: theme.transitions.create(['background-color', 'border-color', 'opacity']),
                                  '&:hover': {
                                    borderColor: 'secondary.main',
                                    backgroundColor: isGroupSelected || isGroupPartiallySelected ? 'secondary.main' : alpha(theme.palette.secondary.main, 0.08),
                                  },
                                  '&.Mui-focusVisible': {
                                    outline: `2px solid ${theme.palette.secondary.main}`,
                                    outlineOffset: 1,
                                  },
                                  '&.Mui-disabled': { opacity: 0.55 },
                                }}
                              >
                                {isGroupSelected ? (
                                  <CheckRoundedIcon sx={{ fontSize: 14 }} />
                                ) : isGroupPartiallySelected ? (
                                  <Box sx={{ width: 8, height: 2, borderRadius: 1, backgroundColor: 'currentColor' }} />
                                ) : null}
                              </ButtonBase>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: 0.5,
                                  minWidth: 0,
                                  flex: 1,
                                  borderRadius: '6px',
                                  textAlign: 'left',
                                }}
                              >
                                <Typography
                                  variant="overline"
                                  noWrap
                                  sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', color: 'text.secondary', lineHeight: 1 }}
                                >
                                  {group.title}
                                </Typography>
                                <Typography variant="caption" noWrap sx={{ fontSize: '0.6875rem', color: 'text.disabled', lineHeight: 1 }}>
                                  {selectedCount > 0
                                    ? `(${selectedCount}/${group.items.length} selecionada${selectedCount !== 1 ? 's' : ''})`
                                    : `(${group.items.length} trecho${group.items.length !== 1 ? 's' : ''})`}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                aria-label={`${isGroupExpanded ? 'Recolher' : 'Expandir'} gravações de ${group.title}`}
                                aria-expanded={isGroupExpanded}
                                onClick={event => {
                                  event.stopPropagation();
                                  setExpandedRecordingGroups(current => ({
                                    ...current,
                                    [group.channelNumber]: !isGroupExpanded,
                                  }));
                                }}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  p: 0,
                                  flexShrink: 0,
                                  color: 'secondary.main',
                                  border: '1px solid',
                                  borderColor: alpha(theme.palette.secondary.main, 0.22),
                                  backgroundColor: alpha(theme.palette.secondary.main, 0.06),
                                  transform: isGroupExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                  transition: theme.transitions.create(['background-color', 'border-color', 'transform']),
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                    borderColor: alpha(theme.palette.secondary.main, 0.36),
                                  },
                                }}
                              >
                                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Box>
                            <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
                              <Stack spacing={0.75}>
                                {group.items.map((recording, index) => {
                                  const recordingTitle = group.title;
                                  const isSelected = selectedRecordingKeys.includes(recordingKey(recording));
                                  return (
                                    <ListItem key={`${recordingKey(recording)}-${index}`} disablePadding>
                                      <ListItemButton
                                        onClick={() => toggleRecordingSelection(recording)}
                                        disabled={playbackControlsLocked}
                                        selected={isSelected}
                                        aria-pressed={isSelected}
                                        aria-label={`${isSelected ? 'Remover seleção de' : 'Selecionar'} ${recordingTitle}`}
                                        sx={{
                                          minHeight: 52,
                                          px: 1.25,
                                          py: 0.75,
                                          gap: 1,
                                          border: '1px solid',
                                          borderColor: isSelected ? alpha(theme.palette.secondary.main, 0.4) : alpha(theme.palette.text.primary, 0.08),
                                          borderRadius: '12px',
                                          backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.08) : 'background.default',
                                          transition: theme.transitions.create(['background-color', 'border-color']),
                                          '&:hover': {
                                            backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.12) : 'action.hover',
                                          },
                                          '&.Mui-selected': {
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                                          },
                                          '&.Mui-selected:hover': {
                                            backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                                          },
                                          '&.Mui-focusVisible': {
                                            outline: `2px solid ${theme.palette.secondary.main}`,
                                            outlineOffset: 1,
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 18,
                                            height: 18,
                                            flexShrink: 0,
                                            border: '1.5px solid',
                                            borderColor: isSelected ? 'secondary.main' : alpha(theme.palette.text.primary, 0.22),
                                            borderRadius: '5px',
                                            color: 'secondary.contrastText',
                                            backgroundColor: isSelected ? 'secondary.main' : 'transparent',
                                          }}
                                        >
                                          {isSelected && <CheckRoundedIcon sx={{ fontSize: 14 }} />}
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, gap: 0.5 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 0.75, color: 'text.secondary' }}>
                                            <CalendarTodayRoundedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              sx={{
                                                color: 'text.primary',
                                                fontWeight: 600,
                                                fontVariantNumeric: 'tabular-nums',
                                                fontSize: '0.7rem',
                                              }}
                                            >
                                              {formatRecordingDateRange(recording.startedAt, recording.endedAt, deviceOffsetMinutes)}
                                            </Typography>

                                            <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                                              •
                                            </Box>

                                            <AccessTimeRoundedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                                            <Typography
                                              variant="caption"
                                              noWrap
                                              sx={{
                                                color: 'text.primary',
                                                fontWeight: 600,
                                                fontVariantNumeric: 'tabular-nums',
                                                fontSize: '0.7rem',
                                              }}
                                            >
                                              {formatRecordingTimeRange(recording.startedAt, recording.endedAt, deviceOffsetMinutes)}
                                            </Typography>
                                          </Box>

                                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 0.5, color: 'text.secondary' }}>
                                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
                                            <Typography variant="caption" noWrap sx={{ fontSize: '0.6rem' }}>
                                              {formatBytes(recording.fileSize)}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </ListItemButton>
                                    </ListItem>
                                  );
                                })}
                              </Stack>
                            </Collapse>
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </Box>
                </Box>
              )}
            </Box>

            {sidebarMode === 'filters' && (
              <Box sx={{ px: SIDEBAR_PADDING, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                {searchHint && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, textAlign: 'center' }}>
                    {searchHint}
                  </Typography>
                )}
                <Tooltip title={searchHint || ''} disableHoverListener={!searchHint} placement="top">
                  <Box>
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      color="secondary"
                      disableElevation
                      startIcon={loadingRecordings ? <CircularProgress size={18} color="inherit" /> : <SearchRoundedIcon />}
                      onClick={() => void loadRecordings()}
                      disabled={!canSearch}
                      sx={{ height: 38, minHeight: 38, px: 1.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                    >
                      {loadingRecordings ? 'Buscando gravações...' : 'Buscar gravações'}
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
            )}

            {sidebarMode === 'recordings' && (
              <Box sx={{ px: SIDEBAR_PADDING, py: 1.5, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  color={isPlaybackActive ? 'error' : 'secondary'}
                  disableElevation
                  startIcon={
                    loadingPlayback ? <CircularProgress size={18} color="inherit" /> : isPlaybackActive ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />
                  }
                  disabled={loadingPlayback || (!isPlaybackActive && !selectedRecordingKeys.length)}
                  onClick={() => (isPlaybackActive ? stopPlayback() : void playSelectedRecordings())}
                  aria-label={isPlaybackActive ? 'Parar reprodução' : 'Reproduzir selecionadas'}
                  sx={{ height: 38, minHeight: 38, px: 1.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
                >
                  {isPlaybackActive
                    ? 'Parar reprodução'
                    : loadingPlayback
                    ? 'Iniciando playback...'
                    : `Reproduzir selecionadas (${selectedRecordingKeys.length})`}
                </Button>
                <MassDownloadButton
                  isDownloading={isDownloading}
                  hasSelection={selectedRecordingKeys.length > 0}
                  onStart={startSelectedDownloads}
                  onCancel={cancelDownloads}
                />
              </Box>
            )}
          </Box>
        ))()}
        {(() => (
          <Box
            component="main"
            aria-label="Playback de câmeras"
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: { xs: '100%', md: playerMaxWidth ? `${playerMaxWidth}px` : '100%' },
              maxHeight: { xs: 'none', md: playerMaxHeight ? `${playerMaxHeight}px` : 'none' },
              justifySelf: 'center',
              alignSelf: 'start',
              minHeight: 0,
              minWidth: 0,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              backgroundColor: 'background.paper',
            }}
          >
            <Box
              sx={{
                width: '100%',
                aspectRatio: '16 / 9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.paper',
                position: 'relative',
              }}
            >
              {playback?.length ? (
                <>
                  <Box ref={playerContainerRef} sx={{ width: '100%', height: '100%' }}>
                    <HatJsVideoPlayer
                      ref={playbackPlayerRef}
                      streams={playback.map(response => {
                        const channel = channels.find(ch => ch.id === response.channelId);
                        return {
                          channelId: response.channelId,
                          title: channel ? channelLabel(channel) : `Câmera ${response.channelId}`,
                          url: response.url,
                          // Hora de parede do dispositivo (evita conversão UTC no player HATJS).
                          playback: {
                            startedAt: toDeviceWallClockSeconds(response.startedAt, deviceOffsetMinutes),
                            endedAt: toDeviceWallClockSeconds(response.endedAt, deviceOffsetMinutes),
                            inactive: response.inactive,
                            ...(response.recordingStartedAt && response.recordingEndedAt
                              ? {
                                  recordingStartedAt: toDeviceWallClockSeconds(response.recordingStartedAt, deviceOffsetMinutes),
                                  recordingEndedAt: toDeviceWallClockSeconds(response.recordingEndedAt, deviceOffsetMinutes),
                                }
                              : {}),
                          },
                        };
                      })}
                      onOsdTime={setCurrentPlaybackTime}
                      quality={quality}
                      qualityChanging={loadingPlayback}
                      onQualityChange={changeQuality}
                      preserveVideoAspectRatio
                      overlayControls
                      enableSpotlightMode
                      showQualityControl={false}
                      title="Playback de câmeras"
                      isFullscreen={isPlayerFullscreen}
                      onFullscreenToggle={togglePlayerFullscreen}
                      controls={
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.75, minWidth: 0 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <CameraPlaybackTimeline
                              recordings={timelineRecordings}
                              rangeStart={timelineRange.start}
                              rangeEnd={timelineRange.end}
                              currentTime={currentPlaybackTime}
                              activeRecording={activeRecording}
                              // A timeline de scrub deve continuar interativa DURANTE o playback ativo:
                              // é justamente quando o usuário quer clicar/arrastar para pular no tempo.
                              // Só travamos enquanto o trecho está carregando (evita buscas concorrentes).
                              // Não usamos playbackControlsLocked aqui (que também é true com playback ativo,
                              // apropriado apenas para os controles da sidebar).
                              disabled={loadingPlayback}
                              variant="overlay"
                              timeZoneOffsetMinutes={deviceOffsetMinutes}
                              channelLabels={channelLabelsByNumber}
                              onSelect={selectTimelinePoint}
                            />
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              width: '1px',
                              height: '24px',
                              backgroundColor: 'rgba(255,255,255,0.28)',
                              flexShrink: 0,
                            }}
                          />
                          <Tooltip title={isPlayerFullscreen ? 'Sair de tela cheia' : 'Tela cheia'}>
                            <span>
                              <IconButton
                                onClick={togglePlayerFullscreen}
                                aria-label={isPlayerFullscreen ? 'Sair de tela cheia' : 'Tela cheia'}
                                size="small"
                                disabled={loadingPlayback}
                                sx={{
                                  flexShrink: 0,
                                  width: '28px',
                                  height: '28px',
                                  color: '#fff',
                                  borderRadius: '6px',
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                                  '&.Mui-disabled': {
                                    color: 'rgba(255,255,255,0.4)',
                                    borderColor: 'rgba(255,255,255,0.24)',
                                  },
                                }}
                              >
                                {isPlayerFullscreen ? <FullscreenExitRoundedIcon fontSize="medium" /> : <FullscreenRoundedIcon fontSize="medium" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </Box>

                  {loadingPlayback && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 1,
                        color: 'common.white',
                        backgroundColor: theme.palette.common.black,
                        zIndex: 3,
                      }}
                    >
                      <CircularProgress color="inherit" size={28} />
                      <Typography variant="caption">Carregando o trecho selecionado…</Typography>
                    </Box>
                  )}
                </>
              ) : loadingPlayback ? (
                <Stack alignItems="center" spacing={1.5}>
                  <CircularProgress color="inherit" size={28} sx={{ color: onVideoDisabled }} />
                  <Typography variant="caption" sx={{ color: onVideoSecondary }}>
                    Carregando playback…
                  </Typography>
                </Stack>
              ) : (
                <Stack alignItems="center" spacing={1.5} sx={{ textAlign: 'center', px: 3, py: 4, maxWidth: 380 }}>
                  <VideocamOffRoundedIcon sx={{ fontSize: 48, color: onVideoDisabled }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: onVideoPrimary }}>
                    Nenhum playback iniciado
                  </Typography>
                  <Typography variant="body2" sx={{ color: onVideoSecondary }}>
                    {recordings.length
                      ? 'Selecione uma gravação na seção de gravações para reproduzir.'
                      : 'Escolha o veículo, selecione as câmeras e busque as gravações.'}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>
        ))()}
      </Box>
      <Snackbar
        open={Boolean(downloadNotice)}
        autoHideDuration={6000}
        onClose={() => setDownloadNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setDownloadNotice(null)} sx={{ width: '100%' }}>
          {downloadNotice}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CameraPlayback;
